(() => {
  const DISPLAY_QUERY_PARAM = 'collection_search';
  const SEARCH_SECTION_ID = 'main-search';
  const SEARCH_DEBOUNCE_MS = 360;

  if (customElements.get('regista-collection-browser')) return;

  class RegistaCollectionBrowser extends HTMLElement {
    connectedCallback() {
      this.lifecycleController = new AbortController();
      this.requestController = null;
      this.debounceTimer = null;
      this.sectionId = this.dataset.sectionId;
      this.collectionUrl = new URL(this.dataset.collectionUrl, window.location.origin);
      this.searchUrl = new URL(this.dataset.searchUrl, window.location.origin);
      this.teamFilterName = this.dataset.teamFilterName || '';
      try {
        const values = JSON.parse(this.dataset.teamFilterValues || '[]');
        this.teamFilterValues = Array.isArray(values)
          ? values.map((value) => String(value).trim()).filter(Boolean)
          : [];
      } catch (_error) {
        this.teamFilterValues = [];
      }
      this.searchEnabled = this.hasAttribute('data-search-enabled') && this.teamFilterValues.length > 0;
      this.query = this.searchEnabled
        ? new URL(window.location.href).searchParams.get(DISPLAY_QUERY_PARAM)?.trim() || ''
        : '';

      this.bindEvents();
      this.syncSearchInputs();
      this.hideScopedTeamFacets();
      this.mountMerchandising(Number(this.dataset.currentPage || 1));
      this.updateActiveFilterCount();

      if (this.query) {
        this.loadSearch({ sourceUrl: new URL(window.location.href), historyMode: 'none' });
      }
    }

    disconnectedCallback() {
      this.lifecycleController?.abort();
      this.requestController?.abort();
      window.clearTimeout(this.debounceTimer);
    }

    bindEvents() {
      const signal = this.lifecycleController.signal;

      this.querySelectorAll('[data-collection-search-form]').forEach((form) => {
        form.addEventListener('submit', (event) => this.onSearchSubmit(event), { signal });
      });

      this.querySelectorAll('[data-collection-search-input]').forEach((input) => {
        input.addEventListener('input', (event) => this.onSearchInput(event), { signal });
      });

      this.querySelectorAll('[data-regista-sort]').forEach((select) => {
        select.addEventListener('change', (event) => this.onSortChanged(event), { signal });
      });

      this.querySelectorAll('[data-clear-all]').forEach((button) => {
        button.addEventListener('click', () => this.clearAll(), { signal });
      });

      this.querySelectorAll('[data-clear-search]').forEach((button) => {
        button.addEventListener('click', () => this.clearSearch(), { signal });
      });

      this.querySelectorAll('[data-clear-filters]').forEach((button) => {
        button.addEventListener('click', (event) => this.clearFilters(event), { signal });
      });

      document.addEventListener('facet:update', (event) => this.onFacetUpdate(event), {
        capture: true,
        signal
      });

      window.addEventListener('popstate', () => this.onPopState(), { signal });
    }

    onSearchInput(event) {
      const hasCommittedQuery = new URL(window.location.href).searchParams.has(DISPLAY_QUERY_PARAM);
      this.query = event.currentTarget.value.trim();
      this.syncSearchInputs(event.currentTarget);
      window.clearTimeout(this.debounceTimer);
      this.requestController?.abort();

      this.setStatus(this.query ? 'מחפשים באוסף הזה' : 'מחזירים את כל החולצות');

      this.debounceTimer = window.setTimeout(() => {
        if (this.query) {
          const historyMode = hasCommittedQuery ? 'replace' : 'push';
          this.loadSearch({ sourceUrl: this.currentFilterUrl(), historyMode });
        } else {
          this.loadCollectionFromSearchFilters({ historyMode: 'push' });
        }
      }, SEARCH_DEBOUNCE_MS);
    }

    onSearchSubmit(event) {
      event.preventDefault();
      window.clearTimeout(this.debounceTimer);
      this.query = event.currentTarget.querySelector('[data-collection-search-input]')?.value.trim() || '';
      this.syncSearchInputs();

      if (this.query) {
        this.loadSearch({ sourceUrl: this.currentFilterUrl(), historyMode: 'push' });
      } else {
        this.loadCollectionFromSearchFilters({ historyMode: 'push' });
      }
    }

    onSortChanged(event) {
      const value = event.currentTarget.value;
      this.querySelectorAll('[data-regista-sort]').forEach((select) => {
        select.value = value;
      });

      const drawer = event.currentTarget.closest('facets-drawer');
      if (drawer) {
        const form = drawer.querySelector('facets-form form');
        if (!form) return;

        let sortInput = form.querySelector('input[name="sort_by"]');
        if (!sortInput) {
          sortInput = document.createElement('input');
          sortInput.type = 'hidden';
          sortInput.name = 'sort_by';
          form.append(sortInput);
        }

        sortInput.value = value;
        sortInput.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const sourceUrl = this.currentFilterUrl();
      sourceUrl.searchParams.set('sort_by', value);
      sourceUrl.searchParams.delete('page');

      if (this.query) {
        this.loadSearch({ sourceUrl, historyMode: 'push' });
      } else {
        const collectionRequest = this.toCollectionRequestUrl(sourceUrl);
        this.loadCollection({ requestUrl: collectionRequest, historyMode: 'push' });
      }
    }

    onFacetUpdate(event) {
      const eventUrl = event.detail?.url;
      if (!eventUrl) return;

      const url = eventUrl instanceof URL ? new URL(eventUrl) : new URL(eventUrl, window.location.origin);
      const eventSectionId = url.searchParams.get('section_id');

      if (eventSectionId !== this.sectionId && eventSectionId !== SEARCH_SECTION_ID) return;

      event.preventDefault?.();
      event.stopImmediatePropagation();

      if (this.query || url.pathname === this.searchUrl.pathname) {
        this.syncSearchInputs();
        this.loadSearch({ sourceUrl: url, historyMode: 'push' });
      } else {
        this.loadCollection({ requestUrl: this.toCollectionRequestUrl(url), historyMode: 'push' });
      }
    }

    onPopState() {
      window.clearTimeout(this.debounceTimer);
      const locationUrl = new URL(window.location.href);
      this.query = this.searchEnabled ? locationUrl.searchParams.get(DISPLAY_QUERY_PARAM)?.trim() || '' : '';
      this.syncSearchInputs();

      if (this.query) {
        this.loadSearch({ sourceUrl: locationUrl, historyMode: 'none' });
      } else {
        this.loadCollection({ requestUrl: this.toCollectionRequestUrl(locationUrl), historyMode: 'none' });
      }
    }

    syncSearchInputs(sourceInput) {
      this.querySelectorAll('[data-collection-search-input]').forEach((input) => {
        if (input !== sourceInput && input.value !== this.query) input.value = this.query;
      });
    }

    currentFilterUrl() {
      const visibleForm = Array.from(this.querySelectorAll('facets-form form')).find((form) => {
        return form.offsetParent !== null;
      }) || this.querySelector('facets-form form');

      const url = new URL(this.query ? this.searchUrl : this.collectionUrl);

      if (visibleForm) {
        const formData = new FormData(visibleForm);
        formData.forEach((value, key) => {
          if (!value || key === 'q' || key === 'page' || key === this.teamFilterName) return;
          url.searchParams.append(key, value);
        });
      } else {
        const locationParams = new URL(window.location.href).searchParams;
        locationParams.forEach((value, key) => {
          if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') {
            url.searchParams.append(key, value);
          }
        });
      }

      const selectedSort = this.querySelector('[data-regista-sort]')?.value;
      if (selectedSort) url.searchParams.set('sort_by', selectedSort);

      return url;
    }

    buildNativeSearchQuery(query) {
      const escapedQuery = query
        .trim()
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/([:()])/g, '\\$1');
      const value = /\s/.test(escapedQuery) ? `"${escapedQuery}"` : escapedQuery;

      return `title:${value} OR tag:${value}`;
    }

    toSearchRequestUrl(sourceUrl) {
      const source = sourceUrl instanceof URL ? sourceUrl : new URL(sourceUrl, window.location.origin);
      const requestUrl = new URL(this.searchUrl);

      source.searchParams.forEach((value, key) => {
        if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') {
          if (key !== this.teamFilterName) requestUrl.searchParams.append(key, value);
        }
      });

      requestUrl.searchParams.set('section_id', SEARCH_SECTION_ID);
      requestUrl.searchParams.set('q', this.query ? this.buildNativeSearchQuery(this.query) : '*');
      requestUrl.searchParams.set('type', 'product');
      requestUrl.searchParams.set('options[prefix]', 'last');

      if (this.teamFilterName && this.teamFilterValues.length) {
        requestUrl.searchParams.delete(this.teamFilterName);
        this.teamFilterValues.forEach((value) => {
          requestUrl.searchParams.append(this.teamFilterName, value);
        });
      }

      return requestUrl;
    }

    toCollectionRequestUrl(sourceUrl) {
      const source = sourceUrl instanceof URL ? sourceUrl : new URL(sourceUrl, window.location.origin);
      const requestUrl = new URL(this.collectionUrl);

      source.searchParams.forEach((value, key) => {
        if ((key.startsWith('filter.') && key !== this.teamFilterName) || key === 'sort_by' || key === 'page') {
          requestUrl.searchParams.append(key, value);
        }
      });

      requestUrl.searchParams.set('section_id', this.sectionId);
      return requestUrl;
    }

    async loadSearch({ sourceUrl, historyMode }) {
      if (!this.searchEnabled || !this.query) {
        await this.loadCollectionFromSearchFilters({ historyMode });
        return;
      }

      const requestUrl = this.toSearchRequestUrl(sourceUrl);
      const documentFragment = await this.fetchDocument(requestUrl);
      if (!documentFragment) return;

      const source = documentFragment.querySelector('[data-regista-search-fragment]');
      if (!source) {
        this.finishLoading('לא ניתן היה לטעון את תוצאות החיפוש');
        return;
      }

      const count = Number(source.dataset.registaResultCount || 0);
      const sourceProductList = source.querySelector('product-list');
      const targetProductList = this.querySelector('[data-regista-product-list]');

      if (targetProductList) {
        targetProductList.replaceChildren(
          ...Array.from(sourceProductList?.children || []).map((child) => document.importNode(child, true))
        );
        targetProductList.hidden = count === 0;
      }

      this.replaceFacetRegion(
        '[data-regista-sidebar-facets]',
        source.querySelector('.facets-sidebar facets-form')
      );
      this.replaceFacetRegion(
        '[data-regista-drawer-facets]',
        source.querySelector('facets-drawer facets-form')
      );
      this.replaceRegion('[data-regista-active-facets]', source.querySelector('.active-facets'));
      this.replaceRegion('[data-regista-pagination]', source.querySelector('.pagination'));

      this.removeScopedTeamActiveFacet();
      this.updateResultState(count);
      this.syncSortControls(requestUrl.searchParams.get('sort_by'));
      this.hideScopedTeamFacets();
      this.updateActiveFilterCount();
      this.mountMerchandising(Number(requestUrl.searchParams.get('page') || 1));
      this.commitSearchHistory(requestUrl, historyMode);
      this.finishLoading(`נמצאו ${count} חולצות`);
    }

    async loadCollection({ requestUrl, historyMode }) {
      const documentFragment = await this.fetchDocument(requestUrl);
      if (!documentFragment) return;

      const newSection = documentFragment.querySelector('.shopify-section');
      const currentSection = this.closest('.shopify-section');

      if (!newSection || !currentSection) {
        this.finishLoading('לא ניתן היה לעדכן את המוצרים');
        return;
      }

      this.commitCollectionHistory(requestUrl, historyMode);
      currentSection.replaceChildren(...Array.from(newSection.childNodes).map((node) => document.importNode(node, true)));
    }

    async loadCollectionFromSearchFilters({ historyMode }) {
      const sourceUrl = this.currentFilterUrl();
      const requestUrl = this.toCollectionRequestUrl(sourceUrl);
      await this.loadCollection({ requestUrl, historyMode });
    }

    async fetchDocument(url) {
      this.requestController?.abort();
      this.requestController = new AbortController();
      this.setAttribute('aria-busy', 'true');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: this.requestController.signal
        });

        if (!response.ok) throw new Error(`Collection request failed with ${response.status}`);
        return new DOMParser().parseFromString(await response.text(), 'text/html');
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.finishLoading('לא ניתן היה לעדכן את המוצרים. נסו שוב.');
        }
        return null;
      } finally {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }

    replaceFacetRegion(targetSelector, sourceElement) {
      const target = this.querySelector(targetSelector);
      if (!target) return;

      target.replaceChildren();
      if (sourceElement) target.append(document.importNode(sourceElement, true));
    }

    replaceRegion(targetSelector, sourceElement) {
      const target = this.querySelector(targetSelector);
      if (!target) return;

      target.replaceChildren();
      if (sourceElement) target.append(document.importNode(sourceElement, true));
    }

    updateResultState(count) {
      this.querySelectorAll('[data-result-count]').forEach((element) => {
        element.textContent = `מציגים ${count} חולצות`;
      });

      const emptyState = this.querySelector('[data-empty-results]');
      if (emptyState) emptyState.hidden = count !== 0;
    }

    syncSortControls(value) {
      if (!value) return;
      this.querySelectorAll('[data-regista-sort]').forEach((select) => {
        if (Array.from(select.options).some((option) => option.value === value)) select.value = value;
      });
    }

    hideScopedTeamFacets() {
      if (!this.teamFilterName) return;
      this.querySelectorAll('.regista-facet-group').forEach((group) => {
        if (group.dataset.filterParam === this.teamFilterName) group.hidden = true;
      });
    }

    removeScopedTeamActiveFacet() {
      if (!this.teamFilterValues.length) return;
      const expectedValues = this.teamFilterValues.map((value) => value.toLocaleLowerCase());

      this.querySelectorAll('[data-regista-active-facets] .removable-facet').forEach((facet) => {
        const text = facet.querySelector('.text-subdued')?.textContent.trim().toLocaleLowerCase() || '';
        const isScopedTeam = expectedValues.some((expected) => {
          return text === expected || text.endsWith(`: ${expected}`);
        });
        if (isScopedTeam) facet.remove();
      });

      this.querySelectorAll('[data-regista-active-facets] .active-facets').forEach((activeFacets) => {
        if (!activeFacets.querySelector('.removable-facet, .facets-clear-all')) activeFacets.remove();
      });
    }

    updateActiveFilterCount() {
      const form = Array.from(this.querySelectorAll('facets-form form')).find((candidate) => candidate.offsetParent !== null)
        || this.querySelector('facets-form form');
      let count = 0;

      if (form) {
        count += Array.from(form.querySelectorAll('input[type="checkbox"]:checked'))
          .filter((input) => input.name !== this.teamFilterName)
          .length;
        const hasPrice = Array.from(form.querySelectorAll('input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]'))
          .some((input) => input.value !== '');
        if (hasPrice) count += 1;
      }

      this.querySelectorAll('[data-active-filter-count]').forEach((badge) => {
        badge.textContent = String(count);
        badge.hidden = count === 0;
      });
    }

    mountMerchandising(page) {
      const productList = this.querySelector('[data-regista-product-list]');
      const template = this.querySelector('[data-regista-merchandising-template]');
      if (!productList) return;

      productList.querySelectorAll('[data-regista-merchandising-slot]').forEach((slot) => slot.remove());
      if (!template || page !== 1) return;

      const cards = Array.from(productList.children).filter((child) => child.matches('product-card'));
      if (cards.length < 8) return;

      const requestedPosition = Number(this.dataset.merchandisingPosition || 12);
      const position = Math.min(Math.max(requestedPosition, 8), cards.length);
      const fragment = template.content.cloneNode(true);
      cards[position - 1].after(fragment);
    }

    clearAll() {
      window.clearTimeout(this.debounceTimer);
      this.query = '';
      this.syncSearchInputs();
      const requestUrl = new URL(this.collectionUrl);
      requestUrl.searchParams.set('section_id', this.sectionId);
      this.loadCollection({ requestUrl, historyMode: 'push' });
    }

    clearSearch() {
      window.clearTimeout(this.debounceTimer);
      this.query = '';
      this.syncSearchInputs();
      this.loadCollectionFromSearchFilters({ historyMode: 'push' });
    }

    clearFilters(event) {
      window.clearTimeout(this.debounceTimer);

      const drawer = event?.currentTarget.closest('facets-drawer');
      if (drawer?.hasAttribute('open')) {
        const form = drawer.querySelector('facets-form form');
        const changedInputs = [];

        form?.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
          if (input.name === this.teamFilterName) return;
          input.checked = false;
          changedInputs.push(input);
        });

        form?.querySelectorAll('input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]').forEach((input) => {
          if (input.value === '') return;
          input.value = '';
          changedInputs.push(input);
        });

        changedInputs[0]?.dispatchEvent(new Event('change', { bubbles: true }));
        drawer.hide();
        return;
      }

      if (this.query) {
        const requestUrl = new URL(this.searchUrl);
        const selectedSort = this.querySelector('[data-regista-sort]')?.value;
        if (selectedSort) requestUrl.searchParams.set('sort_by', selectedSort);
        this.loadSearch({ sourceUrl: requestUrl, historyMode: 'push' });
      } else {
        const requestUrl = new URL(this.collectionUrl);
        const selectedSort = this.querySelector('[data-regista-sort]')?.value;
        if (selectedSort) requestUrl.searchParams.set('sort_by', selectedSort);
        requestUrl.searchParams.set('section_id', this.sectionId);
        this.loadCollection({ requestUrl, historyMode: 'push' });
      }
    }

    commitSearchHistory(requestUrl, mode) {
      if (mode === 'none') return;
      const displayUrl = new URL(this.collectionUrl);

      requestUrl.searchParams.forEach((value, key) => {
        if ((key.startsWith('filter.') && key !== this.teamFilterName) || key === 'sort_by' || key === 'page') {
          displayUrl.searchParams.append(key, value);
        }
      });

      displayUrl.searchParams.set(DISPLAY_QUERY_PARAM, this.query);
      this.commitHistory(displayUrl, mode);
    }

    commitCollectionHistory(requestUrl, mode) {
      if (mode === 'none') return;
      const displayUrl = new URL(requestUrl);
      displayUrl.searchParams.delete('section_id');
      displayUrl.searchParams.delete(DISPLAY_QUERY_PARAM);
      this.commitHistory(displayUrl, mode);
    }

    commitHistory(url, mode) {
      const method = mode === 'replace' ? 'replaceState' : 'pushState';
      window.history[method]({ registaCollectionBrowser: true }, '', url.toString());
    }

    setStatus(message) {
      const status = this.querySelector('[data-search-status]');
      if (status) status.textContent = message;
    }

    finishLoading(message) {
      this.removeAttribute('aria-busy');
      this.setStatus(message);
    }
  }

  customElements.define('regista-collection-browser', RegistaCollectionBrowser);
})();
